import {useEffect, useMemo, useState} from 'react'
import {useParams} from 'react-router-dom'

import AppsIcon from '@mui/icons-material/Apps'
import Button from '@mui/material/Button'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlineOutlined'
import Card from '@mui/material/Card'
import CardHeader from '@mui/material/CardHeader'
import CardActions from '@mui/material/CardActions'
import CardContent from '@mui/material/CardContent'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import LockIcon from '@mui/icons-material/Lock'
import LockOpenIcon from '@mui/icons-material/LockOpen';
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import Loader from '../../shared/Loader'
import Modal from '../../shared/Modal'
import NewVersion from './NewVersion'

import {connect} from '../../../state/hooks/tools'
import {filterRecordsByAttribute, formatDate, findRecordByAttribute} from '../../../lib/data'
import {isEmpty} from '../../../lib/tools'
import {createResource, readEndpoint, updateResource} from '../../../lib/asyncMethods'
import {SETTINGS, VERSIONS} from '../../../state/actionTypes'

const Versions = props => {
  const [showModal, setShowModal] = useState(false)
  const params = useParams()
  const versions = filterRecordsByAttribute({records: props.versions, attribute: 'applicationId', id: params.id})
  const applicationSettings = useMemo(() => filterRecordsByAttribute({records: props.settings, attribute: 'ownableId', id: params.id}), [props.settings, params.id])
  const currentSetting = useMemo(() => findRecordByAttribute({records: applicationSettings, attribute: 'name', id: 'METADATA_VERSION'}), [applicationSettings])

  useEffect(() => {
    if (isEmpty(versions))
      props.readEndpoint(`metadata/versions?applicationId=${params.id}`)
    if (isEmpty(props.settings))
      props.readSettings('settings')
  }, [])

  const toggleLockStatus = id => () => {
    const version = findRecordByAttribute({records: versions, attribute: 'id', id})

    props.updateResource({endpoint: `metadata/versions/${version.id}`, attributes: {locked: !version.locked}})
  }

  const setApplicationVersion = versionId => () => {
    if (currentSetting.value === versionId)
      return

    const attributes = {featureFlag: false, value: versionId, ownableId: params.id}

    if (isEmpty(currentSetting))
      props.createSetting({endpoint: 'settings', attributes})
    else
      props.updateSetting({endpoint: `settings/${currentSetting.id}`, attributes})
  }

  const toggleModal = () => {
    setShowModal(!showModal)
  }

  const handleSubmit = data => {
    props.createResource({endpoint: 'metadata/versions', attributes: data}).then(response => {
      if (response.success)
        toggleModal()
    })
  }

  return (
    <Loader loading={isEmpty(versions)}>
      <Stack className='versions' sx={{width: '60%'}}>
        <Stack sx={{flexDirection: 'row', justifyContent: 'space-between'}}>
          <Stack className='label' sx={{flexDirection: 'row'}}>
            <AppsIcon fontSize='large' />
            <Typography variant='h5'>Versions</Typography>
          </Stack>
          <Button onClick={toggleModal}>New Version</Button>
        </Stack>
        <Divider />
        <Grid container rowSpacing={1} columnSpacing={{xs: 1, sm: 2, md: 3}} sx={{marginTop: '15px'}}>
          {
            versions.map(version => {
              const isCurrentApplicationVersion = version.id === currentSetting.value

              return (
                <Grid key={version.id} size={6}>
                  <Card sx={{bgcolor: 'Background'}}>
                    <CardHeader component='h6' title={version.name} />
                    <Divider />
                    <CardContent>
                      <Typography variant='body1'>{version.description}</Typography>
                      <Typography variant='body1'>Locked: {version.locked ? 'Yes' : 'No'}</Typography>
                      <Typography variant='body1'>Application Version: {isCurrentApplicationVersion ? 'Yes' : 'No'}</Typography>
                      <Typography variant='body1'>Last updated at: {formatDate(version.updatedAt)}</Typography>
                    </CardContent>
                    <CardActions>
                      <IconButton onClick={toggleLockStatus(version.id)} aria-label='versions' color={version.locked ? 'success' : 'error'}>
                        {version.locked ? <LockIcon /> : <LockOpenIcon />}
                      </IconButton>
                      <Tooltip title={isCurrentApplicationVersion ? 'Current application version' : 'Set as application version'}>
                        <span>
                          <IconButton
                            onClick={setApplicationVersion(version.id)}
                            aria-label='set-application-version'
                            disabled={isCurrentApplicationVersion}
                            color={isCurrentApplicationVersion ? 'success' : 'primary'}>
                              <CheckCircleOutlineIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Typography variant='caption' sx={{marginLeft: 0.5}}>
                        {isCurrentApplicationVersion ? 'Current' : 'Set version'}
                      </Typography>
                    </CardActions>
                  </Card>
                </Grid>
              )
            })
          }
        </Grid>
      </Stack>
      <Modal maxWidth='lg' open={showModal} handleClose={toggleModal} title='Create New Version' buttonText='Done'>
        <NewVersion handleSubmit={handleSubmit} versions={versions} loading={props.loading} />
      </Modal>
    </Loader>
  )
}

const mapStateToProps = ({metadata}) => ({...metadata})

const actionCreators = ({metadata}) => ({
  readEndpoint: readEndpoint(metadata, VERSIONS),
  createResource: createResource(metadata, VERSIONS),
  readSettings: readEndpoint(metadata, SETTINGS),
  createSetting: createResource(metadata, SETTINGS),
  updateSetting: updateResource(metadata, SETTINGS),
  updateResource: updateResource(metadata, VERSIONS)
})

export default connect(mapStateToProps, actionCreators)(Versions)
